import PropTypes from 'prop-types';
import { Navigate, Outlet } from 'react-router-dom';
import { usePermission } from '@features/auth/hooks/usePermission';

/**
 * Route wrapper with role-based permission protection
 *
 * This component protects routes based on user permissions. If the user doesn't have
 * the required permission, they are redirected to a specified page (default: home).
 *
 * @param {Object} props
 * @param {Array<string>} [props.permission] - Required permission from PERMISSIONS
 * @param {string} [props.role] - Required role/group from GROUPS
 * @param {Array<string>} [props.anyRole] - User must have at least one of these roles
 * @param {string} [props.redirectTo='/'] - Path to redirect if permission denied
 *
 * @example
 * // Protect superadmin routes
 * <Route element={<PermissionRoute permission={PERMISSIONS.CROSS_ACCOUNT_ACCESS} />}>
 *   <Route path="/superadmin/users" element={<SuperAdminUsersPage />} />
 * </Route>
 *
 * @example
 * // Protect feature routes
 * <Route element={<PermissionRoute permission={PERMISSIONS.MANAGE_EMERGENCY} />}>
 *   <Route path="/emergency" element={<EmergencyPage />} />
 * </Route>
 *
 * @example
 * // Custom redirect
 * <Route element={<PermissionRoute permission={PERMISSIONS.MANAGE_EMERGENCY} redirectTo="/forbidden" />}>
 *   <Route path="/admin" element={<AdminPage />} />
 * </Route>
 */
export function PermissionRoute({ permission, role, anyRole, redirectTo = '/' }) {
  const { can, hasRole, hasAnyRole } = usePermission();

  let hasAccess = false;

  // Check permission with hierarchy (preferred)
  if (permission) {
    hasAccess = can(permission);
  }
  // Check specific role
  else if (role) {
    hasAccess = hasRole(role);
  }
  // Check any of the roles
  else if (anyRole) {
    hasAccess = hasAnyRole(anyRole);
  }
  // No permission check specified - deny access
  else {
    console.warn('PermissionRoute: No permission, role, or anyRole specified');
    hasAccess = false;
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}

PermissionRoute.propTypes = {
  permission: PropTypes.arrayOf(PropTypes.string),
  role: PropTypes.string,
  anyRole: PropTypes.arrayOf(PropTypes.string),
  redirectTo: PropTypes.string,
};
